import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useSuspenseQuery } from "@tanstack/react-query";

import { getLowerResults } from "~/data/dashboard/dashboard.service";

import { Button } from "../shared/button";
import { StarsIcon } from "../shared/icons/stars";
import { CallContext } from "../shared/contexts/call-context";
import { useCreateUserTestMutation } from "../scenarios/queries/create-user-test-mutation";

export function ImproveSection() {
  const navigate = useNavigate();
  const call = useContext(CallContext);
  const { data } = useSuspenseQuery({
    queryKey: ["lower_results"],
    queryFn: getLowerResults,
    staleTime: Infinity,
  });
  const createUserTestMutation = useCreateUserTestMutation();

  return (
    <section className="rounded-[20px] bg-white shadow-card py-[10px] px-5">
      <div className="flex items-center mb-3">
        <h2 className="text-[#2D2D2D] font-alternative font-semibold text-2xl uppercase">
          Aprimorar{" "}
        </h2>
        <span className="ml-3 text-sm font-alternative bg-[#D3D3D3] text-[#5B5B5B] px-2 rounded w-20 h-5 flex justify-center items-center">
          {data.length > 1 ? `${data.length} tópicos` : `${data.length} tópico`}
        </span>
        <button
          type="button"
          className="ml-auto font-alternative text-sm leading-4 h-10 inline-flex items-center gap-1 text-secondary-2 disabled:opacity-70"
          disabled={createUserTestMutation.isPending}
          onClick={() => {
            createUserTestMutation.mutate(
              { test_type: "ai", is_random: true },
              { onSuccess: (data) => navigate(`/praticas/${data.id}`) }
            );
          }}
        >
          Escolha da IA <StarsIcon />
        </button>
      </div>
      <div className="space-y-3 overflow-y-auto" style={{ maxHeight: 254 }}>
        {data.map((category) => (
          <section key={category.category_id} className="space-y-2">
            <h3 className="pl-2 font-alternative text-base font-semibold uppercase">
              {category.category_name}
            </h3>
            {category.tests.map((test) => (
              <div
                key={test.title}
                className="flex items-center px-5 py-[10px] bg-surface rounded-[10px] border-l-[6px] border-red-500"
              >
                <div className="grid">
                  <h4 className="font-alternative text-sm">{test.title}</h4>
                  <span className="font-alternative text-sm text-[#5B5B5B]">
                    Última tentativa em{" "}
                    {new Date(test.last_try).toLocaleDateString("pt-BR", {
                      dateStyle: "short",
                    })}
                  </span>
                </div>

                <div className="ml-auto pb-1 px-2 gap-1 flex flex-col justify-center items-center">
                  <span className="text-lg leading-6 font-alternative font-medium text-red-400">
                    {test.max_score.toFixed(1)}
                  </span>
                  <span className="text-sm leading-4 font-normal">
                    Melhor nota
                  </span>
                </div>

                <Button
                  variant="primary-1"
                  className="ml-5 w-[120px] h-10"
                  disabled={createUserTestMutation.isPending}
                  onClick={() =>
                    createUserTestMutation.mutate(
                      {
                        test_id: test.id,
                        test_type: "patient",
                        chat_room_id: call?.id, // links call id to user test
                      },
                      { onSuccess: (data) => navigate(`/praticas/${data.id}`) }
                    )
                  }
                  loading={
                    createUserTestMutation.isPending &&
                    createUserTestMutation.variables.test_type === "patient" &&
                    createUserTestMutation.variables.test_id === test.id
                  }
                >
                  Praticar
                </Button>
              </div>
            ))}
          </section>
        ))}
      </div>
    </section>
  );
}
